// Piggington's Arcade — Pig Mountain (LB-256/LB-386). Pure game logic only: the track
// generator, the physics, the Greedy Crow's race and raids, zones, power-ups, run goals and
// the localStorage key builders. Plain functions over plain data — no React, no DOM, no
// canvas — so scripts/check-pigmountain.mjs and tests/specs/63-pig-mountain.spec.js can run
// whole games in Node with a seeded RNG. docs/PIG_MOUNTAIN_SCOPE.md is the doc of record.
//
// Law 1 posture (same as lib/snackCatch.js): this game pays NOTHING — no tokens, no Oinks,
// no trophies — and writes NOTHING to household state. Its only persistence is one
// device-local localStorage key per child (the best distance), built by pmBestKeyFor below.
// Nothing in this module (or in PigMountain.jsx, which consumes it) may ever import or touch
// addTransaction or any request/earning path.
//
// How a run works, in one paragraph: the mountain is an endless list of 5 m segments, each
// with a height, a sideways bend and a half-width. The slope pulls the sled downhill and drag
// holds it at a cruising speed; a climb slows it, and a run ENDS when the sled stops on a
// climb. There are no lives — hitting things costs speed. Everything else (boost arrows,
// stored boosts, ramps, spins, near misses, the rocket) exists to carry speed into the next
// hill. Distance in metres, plus bonus metres won from the Crow, is the score.

import { modeForAge } from "./snackCatch.js";

export { modeForAge };

/* ------------------------------ tuning: every feel number lives here ------------------------------ */

// Values as played and approved in the 1–2 Oct 2026 test (two children, several rounds).
// Changing how the game feels should be a change to this object and nothing else.
export const PM = {
  SEG: 5,              // metres per track segment
  DRAW: 70,            // segments generated ahead of the sled
  START_Z: 20,         // where a run starts; distance is measured from here
  HALF: 4.2,           // half the course width in open running
  G: 30,               // how hard a slope pushes or pulls
  K: 0.18,             // drag
  VMAX: 48,
  BASE_GRAD: -0.15,    // the ordinary downhill; cruising speed here is about 25 m/s
  START_V: 14,
  HILL: 0.24,          // first hill's gradient
  HILL_STEEPEN: 0.04,  // each hill is this much steeper than the last...
  HILL_STEEPEN_CAP: 0.5, // ...up to half as steep again
  HILL_LEN: 20,        // first hill, metres
  HILL_LEN_STEP: 4,
  HILL_LEN_CAP: 35,
  GENTLE_HILL: 0.75,   // under-8s: hills at three quarters steepness
  AIR_HILL_DRAG: 0.9,  // a hill slows the sled in the air too, so hopping cannot beat it
  BOOST: 14,
  MAX_BOOSTS: 3,
  MAX_BALLS: 3,
  BUMP_KEEP: 0.35,
  NEAR_BONUS: 2,
  NEAR_WINDOW: 0.8,
  HIT_HALF: 0.34,
  SPIN_BONUS: 10, SPIN_CHAIN_STEP: 3, SPIN_BONUS_CAP: 22, CHAIN_WINDOW: 6,
  MIN_JUMP_V: 1.5,
  JUMP_VY: 8.5, MOGUL_VY: 7, RAMP_VY: 11, RAMP_VY_PER_V: 0.1, LIP_VY: 9, LIP_VY_PER_V: 0.12,
  GRAVITY: 24,
  CREST_DROP: 0.22, CREST_MIN_V: 20,
  OFF_COURSE_DRAG: 14,
  TURN_PUSH: 1.2,
  CROW: 25, CROW_GENTLE: 23, CROW_STEP: 1, CROW_STEP_CAP: 7,
  STUN_S: 1.6, STUN_FACTOR: 0.3,
  RACE_BONUS: 100, HAT_BONUS: 50,
  MISSED_GATE_KEEP: 0.75,
  RAID_S: 9, RAID_DROP_S: 0.9, RAID_DROP_S_GENTLE: 1.3,
  BOULDER_V: 27.5, BOULDER_V_GENTLE: 24.5, BOULDER_S: 10,
  ROCKET_S: 4, ROCKET_V: 44, GIANT_S: 5,
  ZONE_LEN: 1000,
  VEX: 1.7,            // heights are DRAWN this much taller than the physics uses
};

// Zone order repeats for ever. Colours live here with the names so the renderer and any
// future art pass read one table. `ice` makes steering slide; `fog` hides the far view.
export const ZONES = [
  { id: "forest", name: "Pine forest", sky: "#CBE6F4", mtn: "#B3D4E6", val: "#BCD7E3", sA: "#E3EFF6", sB: "#DCEAF3", tA: "#F7FBFD", tB: "#EEF5FA", tree: 1 },
  { id: "lake", name: "Frozen lake", sky: "#D6ECF5", mtn: "#BBD9E8", val: "#C7DFEA", sA: "#EAF4F9", sB: "#E2EFF6", tA: "#C4E6F5", tB: "#B2DDF0", tree: 0.12, ice: true },
  { id: "night", name: "Night run", sky: "#16223F", mtn: "#22335A", val: "#1C2B4D", sA: "#3A4D78", sB: "#35476F", tA: "#7E93C0", tB: "#7388B6", tree: 1, night: true },
  { id: "blizzard", name: "Blizzard", sky: "#E6EEF3", mtn: "#D9E4EB", val: "#DDE7EE", sA: "#EDF3F7", sB: "#E6EEF3", tA: "#FBFDFE", tB: "#F2F7FA", tree: 0.6, fog: true },
  { id: "cave", name: "Ice cave", sky: "#12283F", mtn: "#12283F", val: "#1D3A57", sA: "#3D6A91", sB: "#376287", tA: "#A9D3EA", tB: "#9BC8E2", tree: 0, cave: true },
];

// One goal per run, chosen at the start. Not stored, pays nothing; the results card says
// "Goal done" and that is all (the Help answer promises the arcade gives no trophies).
export const GOALS = [
  { k: "spins", n: 3 },
  { k: "wins", n: 1 },
  { k: "near", n: 5 },
  { k: "airs", n: 6 },
  { k: "hats", n: 1 },
];

const CRASHERS = { rock: 1, log: 1, pine: 1, welly: 1, anvil: 1 };
const SCENERY = { tree: 1, pole: 1, lodge: 1, person: 1, snowman: 1, sign: 1, pillar: 1, lantern: 1, flag: 1 };

// The order the mountain's features come in, repeated for ever. A race is always preceded
// by a "pre" cruise that guarantees snowballs.
const SEQ = ["cruise", "climb", "raid", "gap", "movers", "climb", "fork", "pre", "race", "boulder"];

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

/* ------------------------------ a run ------------------------------ */

// opts.mode  "gentle" | "standard" (modeForAge(effectiveAge(child)))
// opts.rng   () => [0,1): Math.random live, a seeded mulberry32 in checks
// opts.marks [{ dist, mine, name }] — best distances to plant flags at and to announce when
//            passed: the child's own (mine: true) and each sibling's on this device
// opts.test  the `&pmTest=1` preview hook: a short fixed slope straight into a long climb, so
//            a run that is never touched ends in a few seconds at a known place
export function createRun(opts = {}) {
  const rng = opts.rng || Math.random;
  const gentle = opts.mode === "gentle";
  const marks = (opts.marks || []).filter((m) => m && m.dist > 60).map((m) => ({ dist: m.dist, mine: !!m.mine, name: m.name || "", planted: false, passed: false }));
  const R = {
    rng, gentle, test: !!opts.test,
    segs: [], segBase: 0,
    gen: { idx: 0, y: 0, curve: 0, half: PM.HALF, grad: PM.BASE_GRAD, queue: [], phase: 0, climbs: 0, races: 0, cruises: 0 },
    movers: [], marks,
    over: false,
    z: PM.START_Z, x: 0, xv: 0, v: PM.START_V, h: 0, vy: 0,
    air: false, rampAir: false, trick: false, spin: 0,
    crash: 0, boosts: gentle ? 1 : 0, balls: 0, bonus: 0,
    race: null, raid: null, boulder: null, shot: null,
    boostFx: 0, warn: false, warnT: 0, warnHold: 0, gapWarn: false,
    steer: 0, tilt: 0, keyL: false, keyR: false,
    tips: 0, crestT: 0, nearT: 0, chain: 0, chainT: 0,
    ice: false, zone: 0, shield: false, mode: "", modeT: 0,
    stats: { spins: 0, wins: 0, near: 0, airs: 0, hats: 0 },
    goal: GOALS[Math.floor(rng() * GOALS.length)],
    events: [],
  };
  if (R.test) {
    section(R, 6, PM.BASE_GRAD, 0, null);
    section(R, 30, 0.3, 0, null);
    R.v = 9;
  } else {
    section(R, 10, PM.BASE_GRAD, 0, null);
  }
  ensure(R, 4);
  emit(R, "goal", { goal: R.goal.k, n: R.goal.n });
  return R;
}

function emit(R, id, vars) { R.events.push({ id, vars: vars || {} }); }
function rnd(R, a, b) { return a + R.rng() * (b - a); }
function pick(R, a) { return a[Math.floor(R.rng() * a.length)]; }

export function distanceOf(R) { return Math.max(0, Math.floor(R.z - PM.START_Z + R.bonus)); }
export function goalProgress(R) { return Math.min(R.goal.n, R.stats[R.goal.k]); }
export function goalDone(R) { return R.stats[R.goal.k] >= R.goal.n; }

/* ------------------------------ track generation ------------------------------ */

export function segAt(R, i) { return R.segs[i - R.segBase]; }
export function gradAt(R, i) { return (segAt(R, i + 1).y - segAt(R, i).y) / PM.SEG; }
export function groundY(R, z) {
  const i = Math.floor(z / PM.SEG), t = z / PM.SEG - i;
  const a = segAt(R, i), b = segAt(R, i + 1);
  return a.y + (b.y - a.y) * t;
}
export function zoneOf(R) { return ZONES[R.zone]; }

function section(R, n, grad, curve, fn) { R.gen.queue.push({ n, i: 0, grad, curve, half: PM.HALF, fn }); }

function planNext(R) {
  const k = SEQ[R.gen.phase++ % SEQ.length];
  if (k === "climb") climb(R);
  else if (k === "race") race(R);
  else if (k === "gap") gap(R);
  else if (k === "fork") fork(R);
  else cruise(R, k === "pre" ? 16 : Math.floor(rnd(R, 16, 24)), k === "pre", k);
}

// A crevasse across the whole course behind a snow lip. Arrive fast and the lip carries the
// sled over; arrive slowly and it falls in (see step()). A boost arrow comes first.
function gap(R) {
  const len = R.gentle ? 2 : 3, padX = pick(R, [-2.2, 0, 2.2]);
  section(R, 8, PM.BASE_GRAD, 0, (i, s) => { if (i === 3) s.sprites.push({ t: "pad", x: padX, w: 1.8, h: 0 }); });
  section(R, len + 5, -0.12, 0, (i, s) => {
    if (i === 0) s.sprites.push({ t: "lip", x: 0, w: s.half * 2 + 0.6, h: 0.9 });
    else if (i <= len) s.gap = true;
  });
}

// The course widens and a line of pines splits it: one side empty, the other with three
// boost arrows and things to hit between them.
function fork(R) {
  const side = R.rng() < 0.5 ? -1 : 1;
  section(R, 24, PM.BASE_GRAD, 0, (i, s, q) => {
    q.half = i < 21 ? 6 : PM.HALF;
    if (i === 2) { s.sprites.push({ t: "sign", x: -side * 3, w: 1.6, h: 2.0, v: 1 }); s.sprites.push({ t: "sign", x: side * 3, w: 1.6, h: 2.0, v: 0 }); }
    if (i >= 4 && i <= 19) {
      s.sprites.push({ t: "pine", x: 0, w: 1.3, h: 2.6 });
      if (i === 6 || i === 11 || i === 16) s.sprites.push({ t: "pad", x: side * 3, w: 1.8, h: 0 });
      else if (i === 8 || i === 13 || i === 18) s.sprites.push({ t: "rock", x: side * (i === 13 ? 4.4 : 1.9), w: 1.3, h: 0.9 });
      else if (i === 9 || i === 15) s.sprites.push({ t: "log", x: side * 3.6, w: 2.4, h: 0.55 });
    }
  });
}

function cruise(R, n, preRace, kind) {
  const g = R.gen;
  const first = g.cruises++ === 0;
  const rampAt = !preRace && R.rng() < 0.6 ? Math.floor(n / 2) : -1;
  const canAt = first || R.rng() < 0.3 ? 3 + Math.floor(R.rng() * (n - 6)) : -1;
  const snowAt = [2 + Math.floor(R.rng() * (n - 4)), preRace ? n - 3 : -1];
  const mogulAt = !preRace && n >= 16 && R.rng() < 0.7 ? 3 : -1;
  const mx = [[-2.4, 1.2], [-0.6, 2.8], [-2.9, 0.5]];
  const powAt = R.rng() < 0.5 ? 2 + Math.floor(R.rng() * (n - 4)) : -1;
  let nextObs = 3;
  const grads = [PM.BASE_GRAD, PM.BASE_GRAD - 0.11, PM.BASE_GRAD + 0.13];
  const turns = [0, 0.035, -0.035, 0.05, -0.05, 0.065, -0.065];
  section(R, n, PM.BASE_GRAD, pick(R, turns), (i, s, q, idx) => {
    if (i === 2 && kind === "raid") s.sprites.push({ t: "trigRaid", x: 0, w: 99, h: 0 });
    if (i === 2 && kind === "boulder") s.sprites.push({ t: "trigBoulder", x: 0, w: 99, h: 0 });
    if (kind === "movers" && (i === 6 || i === 13)) {
      const dir = R.rng() < 0.5 ? -1 : 1, pen = R.rng() < 0.5;
      for (let m = 0; m < (pen ? 3 : 1); m++) R.movers.push({ z: idx * PM.SEG + m * 2.5, kind: pen ? "penguin" : "skier", dir, v: pen ? 3.2 : 4.2, x: 0, on: false, hit: false, c: Math.floor(R.rng() * 5) });
    }
    if (i === powAt) s.sprites.push({ t: pick(R, ["tinhat", "rocket", "giant"]), x: rnd(R, -3, 3), w: 1.0, h: 1.0 });
    if (i % 5 === 0) q.grad = pick(R, grads);          // rollers
    if (i % 6 === 0 && i) q.curve = pick(R, turns);    // left and right turns
    const nearJump = (rampAt >= 0 && i >= rampAt - 2 && i <= rampAt + 6) || (mogulAt >= 0 && i >= mogulAt - 1 && i <= mogulAt + 4);
    if (mogulAt >= 0 && i >= mogulAt && i < mogulAt + 3) { const m = mx[i - mogulAt]; s.sprites.push({ t: "mogul", x: m[0], w: 1.7, h: 0.6 }); s.sprites.push({ t: "mogul", x: m[1], w: 1.7, h: 0.6 }); }
    else if (i === rampAt) s.sprites.push({ t: "ramp", x: rnd(R, -2.2, 2.2), w: 2.6, h: 1.0 });
    else if (i === canAt) s.sprites.push({ t: "can", x: rnd(R, -3, 3), w: 0.9, h: 0.9 });
    else if (i === snowAt[0] || i === snowAt[1]) s.sprites.push({ t: "snow", x: rnd(R, -3, 3), w: 1.2, h: 0.7 });
    else if (i >= nextObs && !nearJump) {
      const k = pick(R, ["rock", "rock", "log", "log", "pine"]);
      const space = R.gentle ? rnd(R, 5, 8) : rnd(R, 3, 6) - Math.min(1.5, g.climbs * 0.15);
      nextObs = i + Math.max(2, Math.floor(space));
      if (k === "rock") s.sprites.push({ t: "rock", x: rnd(R, -3.5, 3.5), w: 1.3, h: 0.9 });
      else if (k === "log") s.sprites.push({ t: "log", x: rnd(R, -2.8, 2.8), w: 2.4, h: 0.55 });
      else s.sprites.push({ t: "pine", x: rnd(R, -3.5, 3.5), w: 1.3, h: 2.6 });
    }
  });
}

// The gradient and length of the nth hill (0-based). Exported so the check can hold "later
// hills are steeper than earlier ones" against the same arithmetic the generator uses.
export function hillFor(n, gentle) {
  return {
    len: PM.HILL_LEN + Math.min(PM.HILL_LEN_CAP, n * PM.HILL_LEN_STEP),
    grad: PM.HILL * (gentle ? PM.GENTLE_HILL : 1) * (1 + Math.min(PM.HILL_STEEPEN_CAP, n * PM.HILL_STEEPEN)),
  };
}

function climb(R) {
  const c = R.gen.climbs++;
  const hill = hillFor(c, R.gentle);
  const padAt = c < 3 || R.rng() < 0.75 ? 3 : -1;
  const padX = pick(R, [-2.2, 0, 2.2]);
  section(R, 8, PM.BASE_GRAD, 0, (i, s) => { if (i === padAt) s.sprites.push({ t: "pad", x: padX, w: 1.8, h: 0 }); });
  section(R, Math.ceil(hill.len / PM.SEG), hill.grad, pick(R, [0, 0.03, -0.03]), null);
  section(R, 8, -0.3, 0, null);
}

// A 320 m slalom of six gates. The course narrows and widens along it.
function race(R) {
  const g = R.gen;
  const sign = R.rng() < 0.5 ? 1 : -1;
  const gates = [10, 19, 28, 37, 46, 55];
  const r = { startZ: 0, finishZ: (g.idx + 62) * PM.SEG, n: g.races++ };
  section(R, 64, PM.BASE_GRAD, 0, (i, s, q, idx) => {
    const gi = gates.indexOf(i);
    if (i >= 6 && i % 8 === 6) q.half = pick(R, [2.8, 2.8, 4.2, 5.6]);
    if (i % 10 === 5) q.curve = pick(R, [0, 0.03, -0.03]);
    const gx = Math.min(2.0, g.half - 1.0);
    if (i === 2) { r.startZ = idx * PM.SEG; s.sprites.push({ t: "start", x: 0, w: 6.4, h: 2.4, race: r }); }
    else if (i === 62) { r.finishZ = idx * PM.SEG; s.sprites.push({ t: "finish", x: 0, w: 6.4, h: 2.6, race: r }); }
    else if (gi >= 0) s.sprites.push({ t: "gate", x: (gi % 2 ? -1 : 1) * sign * gx, w: 2.4, h: 1.7, alt: gi % 2 });
    else if (i === 5 || i === 33) s.sprites.push({ t: "snow", x: 0, w: 1.2, h: 0.7 });
    else if (i === 25 || i === 44) s.sprites.push({ t: "pad", x: sign * gx, w: 1.8, h: 0 });
  });
}

function genSegment(R) {
  const g = R.gen;
  while (!g.queue.length) planNext(R);
  const q = g.queue[0];
  const zi = Math.floor(Math.max(0, g.idx * PM.SEG - PM.START_Z) / PM.ZONE_LEN) % ZONES.length, Z = ZONES[zi];
  const s = { y: g.y, curve: g.curve, half: g.half, zone: zi, gap: false, sprites: [] };
  if (q.fn) q.fn(q.i, s, q, g.idx);
  // scenery either side of the course — never hit, purely to look at
  if (R.rng() < 0.55 * Z.tree) s.sprites.push({ t: "tree", x: -(s.half + rnd(R, 1.5, 7)), w: rnd(R, 1.8, 2.8), h: rnd(R, 3.5, 5.5) });
  if (R.rng() < 0.55 * Z.tree) s.sprites.push({ t: "tree", x: s.half + rnd(R, 1.5, 7), w: rnd(R, 1.8, 2.8), h: rnd(R, 3.5, 5.5) });
  if (Z.cave) { for (let side = -1; side <= 1; side += 2) if (R.rng() < 0.6) s.sprites.push({ t: "pillar", x: side * (s.half + rnd(R, 0.8, 5)), w: rnd(R, 1.2, 2.2), h: rnd(R, 5, 9) }); }
  if (Z.night && g.idx % 3 === 0) { s.sprites.push({ t: "lantern", x: -s.half - 0.7, w: 0.5, h: 2.2 }); s.sprites.push({ t: "lantern", x: s.half + 0.7, w: 0.5, h: 2.2 }); }
  for (const m of R.marks) {
    if (!m.planted && g.idx * PM.SEG >= m.dist + PM.START_Z) { m.planted = true; s.sprites.push({ t: "flag", x: (m.mine ? 1 : -1) * (s.half - 0.8), w: 1.4, h: 2.6, mine: m.mine }); }
  }
  const side = R.rng() < 0.5 ? -1 : 1, r = Z.cave ? 1 : R.rng();
  if (r < 0.035) s.sprites.push({ t: "lodge", x: side * (s.half + rnd(R, 4.5, 8)), w: rnd(R, 4.5, 6), h: rnd(R, 3.2, 4), v: Math.floor(R.rng() * 3) });
  else if (r < 0.12) s.sprites.push({ t: "person", x: side * (s.half + rnd(R, 0.7, 2.6)), w: 0.62, h: 1.7, v: Math.floor(R.rng() * 5), wave: R.rng() < 0.5 });
  else if (r < 0.145) s.sprites.push({ t: "snowman", x: side * (s.half + rnd(R, 0.9, 3)), w: 1.0, h: 1.6 });
  else if (r < 0.165) s.sprites.push({ t: "sign", x: side * (s.half + 0.9), w: 1.1, h: 1.5, v: Math.floor(R.rng() * 2) });
  if (s.sprites.some((o) => o.t === "start" || o.t === "finish")) {
    for (let d = 0; d < 6; d++) s.sprites.push({ t: "person", x: (d % 2 ? -1 : 1) * (s.half + 0.7 + (d >> 1) * 0.75), w: 0.62, h: 1.7, v: d % 5, wave: true });
  }
  if (g.idx % 4 === 0) { s.sprites.push({ t: "pole", x: -s.half - 0.25, w: 0.2, h: 0.9 }); s.sprites.push({ t: "pole", x: s.half + 0.25, w: 0.2, h: 0.9 }); }
  R.segs.push(s);
  g.grad += (q.grad - g.grad) * 0.4;
  g.curve += (q.curve - g.curve) * 0.35;
  g.half += (q.half - g.half) * 0.3;
  g.y += g.grad * PM.SEG;
  g.idx++;
  if (++q.i >= q.n) g.queue.shift();
}

function ensure(R, idx) {
  while (R.gen.idx < idx + PM.DRAW + 12) genSegment(R);
  if (idx - R.segBase > 80) { R.segs.splice(0, 40); R.segBase += 40; }
}

/* ------------------------------ the player's four acts ------------------------------ */

// Touch steering: dMetres is how far the finger moved, already converted to metres at the
// sled's distance by the renderer. On ice most of it becomes slide.
export function steerBy(R, dMetres) {
  if (R.over) return;
  if (R.ice) { R.x += dMetres * 0.4; R.xv += dMetres * 5; } else R.x += dMetres;
  if (dMetres) R.steer = clamp(dMetres * 6, -1, 1);
}

export function jump(R) {
  if (R.over) return;
  // A sled that has all but stopped cannot hop. Without this a child tapping jump on a hill
  // is always in the air when step() looks, the stall is never seen, and the run never ends
  // (found by scripts/check-pigmountain.mjs's hopping player).
  if (!R.air && R.v < PM.MIN_JUMP_V) return;
  if (!R.air) { R.air = true; R.vy = PM.JUMP_VY; R.rampAir = false; R.trick = false; }
  else if (R.rampAir && !R.trick) { R.trick = true; R.spin = 0.001; }
}

export function spendBoost(R) {
  if (R.over || R.boosts < 1) return false;
  R.boosts--; R.v = Math.min(PM.VMAX, R.v + PM.BOOST); R.boostFx = 0.5;
  return true;
}

export function throwBall(R) {
  if (R.over || R.shot) return false;
  if (R.balls < 1) { emit(R, "noBalls"); return false; }
  if (!R.race && !R.raid) { emit(R, "saveBalls"); return false; }
  R.balls--; R.shot = { t: 0 };
  return true;
}

/* ------------------------------ one step of the world ------------------------------ */

// Would the sled, untouched from here, stop on a climb within the next 170 m? Drives the
// red speed bar and the "Hill coming up" banner.
function wouldStall(R) {
  let z = R.z, v = R.v;
  const end = z + 170;
  while (z < end) {
    const i = Math.floor(z / PM.SEG);
    if (!segAt(R, i + 1)) return false;
    const g = gradAt(R, i), dt = PM.SEG / Math.max(v, 2);
    v += (-PM.G * g - PM.K * v) * dt;
    if (v < 1 && g > 0.03) return true;
    if (v < 1) v = 1;
    z += PM.SEG;
  }
  return false;
}

function bump(R, o) {
  if (R.modeT > 0) { o.hit = true; R.v = Math.min(PM.VMAX, R.v + 1); return; }   // rocket / giant snowball smash through
  if (R.crash > 0) return;
  o.hit = true; R.chain = 0;
  if (R.shield) { R.shield = false; R.crash = 0.7; emit(R, "shieldSaved"); return; }
  R.v *= PM.BUMP_KEEP; R.crash = 1.0; emit(R, "bump");
}

function startRace(R, r) {
  if (R.race) return;
  R.raid = null;
  const crow = (R.gentle ? PM.CROW_GENTLE : PM.CROW) + Math.min(PM.CROW_STEP_CAP, r.n * PM.CROW_STEP);   // easy first, harder each race
  R.race = { finishZ: r.finishZ, crowZ: R.z + 4, crowV: crow, stun: 0, passed: 0, seen: 0, lost: false, n: r.n + 1 };
  emit(R, R.balls ? "raceStartBalls" : "raceStartNone");
}

function finishRace(R) {
  if (!R.race) return;
  if (!R.race.lost) { R.bonus += PM.RACE_BONUS; R.stats.wins++; emit(R, "raceWon", { m: PM.RACE_BONUS }); }
  R.race = null; R.shot = null;
}

function hitSprites(R, i) {
  const sp = segAt(R, i).sprites;
  for (let k = 0; k < sp.length; k++) {
    const o = sp[k];
    if (o.hit) continue;
    const t = o.t;
    if (SCENERY[t]) continue;
    if (t === "trigRaid") {
      o.hit = true;
      if (!R.race) { R.raid = { t: 0, x: 0, z: R.z + 45, next: 1.2, hit: false, end: PM.RAID_S }; emit(R, R.balls ? "raidBalls" : "raidNone"); }
      continue;
    }
    if (t === "trigBoulder") { o.hit = true; R.boulder = { z: R.z - 32, v: R.gentle ? PM.BOULDER_V_GENTLE : PM.BOULDER_V, t: 0 }; continue; }
    if (t === "lip") { if (!R.air) { R.air = true; R.rampAir = true; R.trick = false; R.vy = PM.LIP_VY + R.v * PM.LIP_VY_PER_V; } continue; }
    if (t === "start") { startRace(R, o.race); continue; }
    if (t === "finish") { finishRace(R); continue; }
    const near = Math.abs(R.x - o.x) < o.w / 2 + PM.HIT_HALF;
    if (t === "gate") {
      if (!R.race) continue;
      if (Math.abs(R.x - o.x) < 1.25) R.race.passed++;
      else { R.v *= PM.MISSED_GATE_KEEP; emit(R, "missedGate"); }
      R.race.seen++;
      continue;
    }
    if (!near) {
      if (CRASHERS[t] && !R.air && R.nearT <= 0 && Math.abs(R.x - o.x) < o.w / 2 + PM.NEAR_WINDOW) { R.v = Math.min(PM.VMAX, R.v + PM.NEAR_BONUS); R.stats.near++; R.nearT = 0.6; emit(R, "nearMiss"); }
      continue;
    }
    if (t === "tinhat") { o.hit = true; R.shield = true; emit(R, "tinhat"); continue; }
    if (t === "rocket") { o.hit = true; R.mode = "rocket"; R.modeT = PM.ROCKET_S; R.boostFx = 0.5; emit(R, "rocket"); continue; }
    if (t === "giant") { o.hit = true; R.mode = "giant"; R.modeT = PM.GIANT_S; emit(R, "giant"); continue; }
    if (t === "tophat") { o.hit = true; R.bonus += PM.HAT_BONUS; emit(R, "hatCaught", { m: PM.HAT_BONUS }); continue; }
    if (t === "snow") { if (R.balls < PM.MAX_BALLS) { R.balls++; o.hit = true; if (R.tips++ < 2) emit(R, "snowball"); } continue; }
    if (t === "can") { if (R.boosts < PM.MAX_BOOSTS) { R.boosts++; o.hit = true; emit(R, "boostStored"); } continue; }
    if (R.air && R.h > 0.25) { if (t !== "pine") continue; }       // a jump clears everything low; never a pine
    if (t === "pad") { if (!R.air) { R.v = Math.min(PM.VMAX, R.v + PM.BOOST); R.boostFx = 0.5; o.hit = true; } continue; }
    if (t === "mogul") { if (!R.air) { R.air = true; R.rampAir = true; R.trick = false; R.vy = PM.MOGUL_VY; } continue; }
    if (t === "ramp") { if (!R.air) { R.air = true; R.rampAir = true; R.trick = false; R.vy = PM.RAMP_VY + R.v * PM.RAMP_VY_PER_V; R.v = Math.min(PM.VMAX, R.v + 2); } continue; }
    if (!CRASHERS[t]) continue;
    bump(R, o);
  }
}

// Advances the run by dt seconds (the renderer clamps dt to 0.05). Sets R.over when the
// sled stops on a climb. Everything the player should be told lands in R.events.
export function step(R, dt) {
  if (R.over) return;
  const i0 = Math.floor(R.z / PM.SEG);
  ensure(R, i0);
  const s0 = segAt(R, i0), g = gradAt(R, i0), zi = s0.zone;
  if (zi !== R.zone) { R.zone = zi; emit(R, "zone", { zone: ZONES[zi].id }); }
  R.ice = !!ZONES[zi].ice;

  let a;
  if (R.air) a = -0.05 * R.v - PM.G * Math.max(0, g) * PM.AIR_HILL_DRAG;
  else {
    a = -PM.G * g - PM.K * R.v;
    if (Math.abs(R.x) > s0.half) a -= PM.OFF_COURSE_DRAG;
    R.x -= s0.curve * R.v * PM.TURN_PUSH * dt;          // a bend pushes the sled to the outside
  }
  R.v = clamp(R.v + a * dt, 0, PM.VMAX);
  if (R.modeT > 0) { R.modeT -= dt; if (R.mode === "rocket") R.v = Math.max(R.v, PM.ROCKET_V); if (R.modeT <= 0) R.mode = ""; }
  if (!R.air && g > 0.03 && R.v < 0.8) { R.over = true; emit(R, "over"); return; }
  if (!R.air && g <= 0.03 && R.v < 2) R.v = 2;          // never stuck on the flat

  const kd = (R.keyR ? 1 : 0) - (R.keyL ? 1 : 0);
  if (kd) { if (R.ice) R.xv += kd * 16 * dt; else R.x += kd * 8 * dt; R.steer = kd; }
  R.x += R.xv * dt; R.xv *= Math.pow(R.ice ? 0.3 : 0.0001, dt);
  const lim = s0.half + 1.1;
  R.x = clamp(R.x, -lim, lim);
  R.tilt += (R.steer * 0.25 - R.tilt) * Math.min(1, dt * 10);
  R.steer *= Math.pow(0.001, dt);

  R.z += R.v * dt;
  if (R.air) {
    R.vy -= PM.GRAVITY * dt; R.h += R.vy * dt;
    if (R.spin > 0) R.spin = Math.min(Math.PI * 2, R.spin + dt * 9);
    if (R.h <= 0) {
      R.h = 0; R.vy = 0; R.air = false;
      if (R.trick) {
        R.chain++; R.chainT = PM.CHAIN_WINDOW; R.stats.spins++;
        R.v = Math.min(PM.VMAX, R.v + Math.min(PM.SPIN_BONUS_CAP, PM.SPIN_BONUS + PM.SPIN_CHAIN_STEP * (R.chain - 1)));
        R.boostFx = 0.4;
        emit(R, R.chain > 1 ? "spinChain" : "spinLanded", { n: R.chain });
      }
      R.trick = false; R.rampAir = false; R.spin = 0;
    }
  }
  const i1 = Math.floor(R.z / PM.SEG);
  for (let i = i0 + 1; i <= i1; i++) {
    hitSprites(R, i);
    // catch air where the ground falls away, if the sled arrives fast enough
    const drop = gradAt(R, i - 2) - gradAt(R, i);
    if (!R.air && R.crestT <= 0 && drop > PM.CREST_DROP && R.v > PM.CREST_MIN_V) {
      R.air = true; R.rampAir = true; R.trick = false; R.vy = Math.min(12, (R.v - 12) * drop * 1.6); R.crestT = 1.5; R.stats.airs++;
    }
  }
  if (R.crestT > 0) R.crestT -= dt;
  if (R.nearT > 0) R.nearT -= dt;
  if (R.chainT > 0) { R.chainT -= dt; if (R.chainT <= 0) R.chain = 0; }

  // crevasse: land in it, or slide into it, and the sled has to climb out the far side
  if (!R.air && segAt(R, i1).gap) {
    let e = i1;
    while (segAt(R, e) && segAt(R, e).gap) e++;
    R.z = e * PM.SEG + 1; R.v = 3; R.crash = 1.0; R.chain = 0; emit(R, "crevasse");
  }
  R.gapWarn = false;
  if (!R.air && R.v < 21) for (let gi = i1 + 2; gi < i1 + 18; gi++) if (segAt(R, gi) && segAt(R, gi).gap) { R.gapWarn = true; break; }

  // skiers and penguins crossing the course
  for (let mi = R.movers.length - 1; mi >= 0; mi--) {
    const mv = R.movers[mi];
    if (mv.z < R.z - 30) { R.movers.splice(mi, 1); continue; }
    if (!mv.on && mv.z - R.z < 105) { mv.on = true; mv.x = -mv.dir * (PM.HALF + 2.5); }
    if (mv.on) mv.x += mv.dir * mv.v * dt;
    if (!mv.hit && Math.abs(mv.z - R.z) < 1.1 && Math.abs(mv.x - R.x) < 0.85 && !(R.air && R.h > 0.25)) bump(R, mv);
  }

  // the giant snowball rolling down behind
  if (R.boulder) {
    const bo = R.boulder; bo.z += bo.v * dt; bo.t += dt;
    if (bo.z >= R.z - 1.4 && !R.air) {
      if (R.modeT > 0) emit(R, "boulderSmashed"); else { R.v *= 0.3; R.crash = 1.0; R.chain = 0; emit(R, "squished"); }
      R.boulder = null;
    } else if (bo.t > PM.BOULDER_S) { R.boulder = null; emit(R, "boulderOutrun"); }
  }

  // the Crow making mischief between races
  if (R.raid) {
    const rd = R.raid; rd.t += dt; rd.z = R.z + 45; rd.x = Math.sin(rd.t * 1.4) * segAt(R, i1).half * 0.7;
    if (!rd.hit && rd.t >= rd.next) {
      rd.next = rd.t + (R.gentle ? PM.RAID_DROP_S_GENTLE : PM.RAID_DROP_S);
      const ds = segAt(R, Math.floor(rd.z / PM.SEG) + 1);
      if (ds) ds.sprites.push({ t: pick(R, ["welly", "welly", "anvil"]), x: rd.x, w: 1.0, h: 0.9 });
    }
    if (R.shot) {
      R.shot.t += dt;
      if (R.shot.t >= 0.35) {
        R.shot = null; rd.hit = true; rd.end = rd.t + 1.3; R.stats.hats++;
        const hs = segAt(R, Math.floor((R.z + 38) / PM.SEG));
        if (hs) hs.sprites.push({ t: "tophat", x: clamp(rd.x, -3, 3), w: 1.3, h: 1.1 });
        emit(R, "hatKnocked");
      }
    }
    if (rd.t >= rd.end) R.raid = null;
  }

  if (R.crash > 0) R.crash -= dt;
  if (R.boostFx > 0) R.boostFx -= dt;

  if (R.race) {
    const r = R.race;
    if (r.stun > 0) r.stun -= dt;
    r.crowZ += r.crowV * (r.stun > 0 ? PM.STUN_FACTOR : 1) * dt;
    if (!r.lost && r.crowZ >= r.finishZ) { r.lost = true; emit(R, "raceLost"); }
    if (R.shot) { R.shot.t += dt; if (R.shot.t >= 0.35) { R.shot = null; r.stun = PM.STUN_S; emit(R, "crowHit"); } }
  }
  if (R.shot && !R.race && !R.raid) R.shot = null;       // a throw whose target has gone

  R.warnT -= dt; R.warnHold -= dt;
  if (R.warnT <= 0) {
    R.warnT = 0.25;
    if (!R.race && wouldStall(R)) { R.warn = true; R.warnHold = 1.2; } else if (R.warnHold <= 0) R.warn = false;
  }

  // best distances passed: the child's own record, and each sibling's
  const d = distanceOf(R);
  for (const m of R.marks) {
    if (!m.passed && d > m.dist) { m.passed = true; emit(R, m.mine ? "recordPassed" : "siblingPassed", { name: m.name }); }
  }
}

/* ------------------------------ results ------------------------------ */

// What the results card needs, and what (if anything) to store. `prevBest` is the stored
// best distance or 0.
export function settle(R, prevBest) {
  const dist = distanceOf(R);
  const prev = Math.max(0, prevBest | 0);
  const isBest = dist > prev;
  return { dist, best: isBest ? dist : prev, isBest, beatBy: isBest && prev > 0 ? dist - prev : 0, goal: R.goal, goalGot: goalProgress(R), goalDone: goalDone(R) };
}

/* ------------------------------ device-local persistence keys ------------------------------ */

// The game's ONLY persistence: a per-child best distance ({ best, at }), device-local by the
// same accepted cost as Snack Attack's (a best does not follow a child to another device).
// The daily Brain Warm-Up key is shared with the whole arcade and lives in lib/snackCatch.js.
export function pmBestKeyFor(childId) {
  return `lb-pm-best-${childId}`;
}
